import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
import React, { useRef, useEffect, useState } from 'react';
import { Grid } from 'semantic-ui-react';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import { getArrowColumnWidth, getItemsColumnWidth, getSlidingCarouselHeight, renderSlideElements } from './BasicCarousel.utils';
import useResponsive from '../../../../../../hooks/use-responsive';
import { toArray } from '../../../../../../services/components';
import { classNames } from '../../../../../../utils/classname';
import Pagination from '../../../Pagination';
import { displayCarouselIndicators, getAutoScrollInterval, getPageSize, showIndicators } from '../../services';
import { canSafelyServerRender, getSlide, getStepSize } from '../../services/carousel';
import { ARROW_POSITION } from '../../services/constants';
import { BaseCarouselPropTypes, BaseCarouselDefaultProps, DEFAULT_MOBILE_PROPS } from '../../types/base';
import './BasicCarousel.less';
var defaultProps = BaseCarouselDefaultProps;
export default function BasicCarousel(_ref) {
  var _classNames2;
  var items = _ref.items,
    size = _ref.size,
    stepSize = _ref.stepSize,
    grouping = _ref.grouping,
    autoScroll = _ref.autoScroll,
    responsive = _ref.responsive,
    continuous = _ref.continuous,
    pageIndicatorIcon = _ref.pageIndicatorIcon,
    className = _ref.className,
    mobile = _ref.mobile,
    renderItemContainer = _ref.renderItemContainer,
    displayIndicatorsProp = _ref.displayIndicators,
    displayPlayPauseButtonProp = _ref.displayPlayPauseButton,
    displayLeftRightArrows = _ref.displayLeftRightArrows,
    animation = _ref.animation,
    leftArrowIcon = _ref.leftArrowIcon,
    rightArrowIcon = _ref.rightArrowIcon,
    slide = _ref.slide,
    event = _ref.event;
  var carouselRef = useRef(null);
  var responsiveDevice = useResponsive();
  var isDesktop = responsiveDevice.isDesktop,
    isTablet = responsiveDevice.isTablet,
    isMobile = responsiveDevice.isMobile,
    loaded = responsiveDevice.loaded;
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    slidingCarouselHeight = _useState2[0],
    setSlidingCarouselHeight = _useState2[1];
  var mobilePropsWithDefaults = _objectSpread(_objectSpread({}, DEFAULT_MOBILE_PROPS), mobile);
  var displayAllMobile = mobilePropsWithDefaults.displayAll,
    displayAllColumnsMobile = mobilePropsWithDefaults.displayAllColumns,
    arrowsPositionMobile = mobilePropsWithDefaults.arrowsPosition;
  var pageSize = getPageSize(items, size, responsive, mobilePropsWithDefaults, responsiveDevice);
  var finalStepSize = getStepSize(items, pageSize, stepSize, grouping);
  var displayIndicators = displayIndicatorsProp && !!pageIndicatorIcon && (!slide || pageSize === 1) && displayCarouselIndicators(pageSize, items, {
    isMobile: isMobile
  });
  var autoScrollInterval = getAutoScrollInterval(autoScroll, {
    pageSize: pageSize,
    items: items
  });
  var displayPlayPauseButton = displayPlayPauseButtonProp && !_isNil(autoScrollInterval);
  var displayBottomControls = displayIndicators || displayPlayPauseButton;
  var finalSlide = getSlide(slide, isMobile, displayAllMobile);
  var canServerRender = canSafelyServerRender(items, size, responsive, mobilePropsWithDefaults);
  useEffect(function () {
    var observer = null;
    if (finalSlide && carouselRef.current) {
      observer = new MutationObserver(function () {
        setSlidingCarouselHeight(getSlidingCarouselHeight(carouselRef));
      });
      observer.observe(carouselRef.current, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }
    return function () {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [carouselRef, finalSlide]);
  useEffect(function () {
    if (finalSlide && carouselRef.current) {
      setSlidingCarouselHeight(getSlidingCarouselHeight(carouselRef));
    }
  }, [isMobile, isTablet, isDesktop, finalSlide, carouselRef]);
  if (_isEmpty(items)) {
    return null;
  }
  var leftArrow = /*#__PURE__*/React.createElement(Grid.Column, {
    width: getArrowColumnWidth(isMobile, arrowsPositionMobile),
    verticalAlign: "middle",
    textAlign: "left",
    className: "arrow-column left-arrow-column"
  }, /*#__PURE__*/React.createElement(Pagination.Arrow, {
    direction: "previous",
    icon: leftArrowIcon
  }));
  var rightArrow = /*#__PURE__*/React.createElement(Grid.Column, {
    width: getArrowColumnWidth(isMobile, arrowsPositionMobile),
    verticalAlign: "middle",
    textAlign: "right",
    className: "arrow-column right-arrow-column"
  }, /*#__PURE__*/React.createElement(Pagination.Arrow, {
    direction: "next",
    icon: rightArrowIcon
  }));
  var itemsColumnContainerClassName = classNames(_defineProperty({
    'pagination-elements-column': true
  }, "size-".concat(size), true));
  var itemsColumn = /*#__PURE__*/React.createElement(Grid.Column, {
    width: getItemsColumnWidth(isMobile, arrowsPositionMobile, displayLeftRightArrows),
    textAlign: "center",
    className: itemsColumnContainerClassName
  }, /*#__PURE__*/React.createElement(Grid, {
    className: "carousel-elements-grid",
    columns: isMobile && displayAllMobile ? displayAllColumnsMobile : pageSize,
    verticalAlign: "middle",
    textAlign: "center",
    style: finalSlide ? {
      height: slidingCarouselHeight
    } : {}
  }, /*#__PURE__*/React.createElement(Pagination.Elements, null, function (activeElements, _ref2) {
    var activePage = _ref2.activePage,
      getAllItems = _ref2.getAllItems,
      totalPages = _ref2.totalPages;
    if (!loaded && !canServerRender) {
      return null;
    }
    if (finalSlide) {
      return renderSlideElements({
        items: getAllItems(),
        activePage: activePage,
        pageSize: pageSize,
        renderItemContainer: renderItemContainer,
        stepSize: finalStepSize,
        totalPages: totalPages
      });
    }
    return activeElements.map(renderItemContainer);
  })));
  var content = [itemsColumn];
  if (displayLeftRightArrows) {
    content = [leftArrow, itemsColumn, rightArrow];
    if (isMobile) {
      if (arrowsPositionMobile === ARROW_POSITION.top) {
        content = [leftArrow, rightArrow, itemsColumn];
      }
      if (arrowsPositionMobile === ARROW_POSITION.bottom) {
        content = [itemsColumn, leftArrow, rightArrow];
      }
    }
  }
  var finalClassName = classNames((_classNames2 = {
    carousel: true,
    'basic-carousel': true
  }, _defineProperty(_classNames2, className, true), _defineProperty(_classNames2, "slide", finalSlide), _classNames2));
  return /*#__PURE__*/React.createElement("div", {
    className: finalClassName,
    ref: carouselRef
  }, /*#__PURE__*/React.createElement(Pagination, {
    pageSize: pageSize,
    grouping: grouping,
    stepSize: finalStepSize,
    items: toArray(items),
    carousel: true,
    continuous: continuous,
    autoScroll: !!autoScroll,
    autoScrollInterval: autoScrollInterval,
    animation: animation,
    slide: finalSlide,
    event: event
  }, /*#__PURE__*/React.createElement(Grid, {
    className: "main-carousel-grid"
  }, content, displayBottomControls && /*#__PURE__*/React.createElement(Grid.Column, {
    width: 16,
    className: "indicators-column",
    verticalAlign: "top",
    textAlign: "center"
  }, displayIndicators && /*#__PURE__*/React.createElement(Pagination.Indicators, {
    onlyPageIndicators: true,
    pageIndicatorIcon: pageIndicatorIcon,
    show: showIndicators(pageSize, items)
  }), displayPlayPauseButton && /*#__PURE__*/React.createElement(Pagination.Play, null)))));
}
BasicCarousel.defaultProps = defaultProps;